import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface PlatformAdmin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'SUPER_ADMIN' | 'BILLING_ADMIN' | 'SUPPORT' | 'DEVELOPER';
  isPlatformAdmin: true;
}

interface PlatformAdminAuthState {
  admin: PlatformAdmin | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  setAuth: (admin: PlatformAdmin, token: string) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

export const usePlatformAdminAuthStore = create<PlatformAdminAuthState>()(
  persist(
    (set, get) => ({
      admin: null,
      token: null,
      login: async (email: string, password: string) => {
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
        const response = await fetch(`${API_URL}/platform-admin/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ message: 'Login failed' }));
          throw new Error(error.message || 'Invalid credentials');
        }

        const data = await response.json();
        set({
          admin: data.admin,
          token: data.access_token,
        });
      },
      setAuth: (admin, token) => {
        set({ admin, token });
      },
      logout: () => {
        set({ admin: null, token: null });
      },
      isAuthenticated: () => {
        const state = get();
        return !!(state.admin && state.token);
      },
    }),
    {
      name: 'platform-admin-auth-storage', // Different key from org auth
      storage: createJSONStorage(() => localStorage),
    }
  )
);
